import React, { useContext, useMemo, lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Context } from "../../contexts/Store";
import { Loading } from "../../pages/freeCourse/CourseView";

// ✅ Lazy load all pages for better performance
const LoginPage = lazy(() => import("../../pages/login"));
const SuperAdmin = lazy(() => import("../../pages/superAdmin"));
const LeadGeneratorAdmin = lazy(() => import("../../pages/leadGeneratorAdmin"));
const LeadManagerAdmin = lazy(() => import("../../pages/leadManagerAdmin"));
const SalesPerson = lazy(() => import("../../pages/salesPerson"));
const CampusAdmin = lazy(() => import("../../pages/campus"));
const TeamLeader = lazy(() => import("../../pages/teamLeader"));
const FinanceExecutiveIndex = lazy(() => import("../../pages/financeExecutive"));
const FinanceManagerIndex = lazy(() => import("../../pages/financeManager"));

// ✅ Generic Protected Route
const ProtectedRoute = ({ allowedRoles, children }) => {
  const { state } = useContext(Context);
  const userRole = state?.user_details?.role;

  // If user not logged in or unauthorized → redirect to login
  if (!userRole || !allowedRoles.includes(userRole)) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// ✅ Main Router Component
const AppRouter = () => {
  const { state } = useContext(Context);
  const role = state?.user_details?.role;

  // ✅ Memoize redirect path to avoid recalculating on re-renders
  const redirectPath = useMemo(() => {
    switch (role) {
      case "superuser":
      case "lms_admin":
        return "/superadmin/";
      case "lead_manager":
        return "/salesmanager/";
      case "lead_generator":
        return "/leadgenerator/";
      case "sales_person":
        return "/admission-consultant/";
      case "campus_admin":
        return "/campus-admin/";
      case "sales_team_leader":
        return "/team-leader/";
      case "finance_executive":
        return "/finance-executive/";
      case "finance_manager":
        return "/finance-manager/";
      default:
        return "/login";
    }
  }, [role]);

  return (
    <Suspense fallback={<Loading/>}>
      <Routes>
        {/* Root route → auto-redirect based on user role */}
        <Route path="/" element={<Navigate to={redirectPath} replace />} />

        {/* Public Login route */}
        <Route path="/login/*" element={<LoginPage />} />

        {/* Protected routes */}
        <Route
          path="/superadmin/*"
          element={

            <ProtectedRoute allowedRoles={["superuser", "lms_admin"]}>
              <SuperAdmin />
            </ProtectedRoute>
          }
        />

        <Route
          path="/leadgenerator/*"
          element={
            <ProtectedRoute allowedRoles={["lead_generator"]}>
              <LeadGeneratorAdmin />
            </ProtectedRoute>
          }
        />

        <Route
          path="/salesmanager/*"
          element={
            <ProtectedRoute allowedRoles={["lead_manager"]}>
              <LeadManagerAdmin />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admission-consultant/*"
          element={
            <ProtectedRoute allowedRoles={["sales_person"]}>
              <SalesPerson />
            </ProtectedRoute>
          }
        />

        <Route
          path="/campus-admin/*"
          element={
            <ProtectedRoute allowedRoles={["campus_admin"]}>
              <CampusAdmin />
            </ProtectedRoute>
          }
        />

        <Route
          path="/team-leader/*"
          element={
            <ProtectedRoute allowedRoles={["sales_team_leader"]}>
              <TeamLeader />
            </ProtectedRoute>
          }
        />

        <Route
          path="/finance-executive/*"
          element={
            <ProtectedRoute allowedRoles={["finance_executive"]}>
              <FinanceExecutiveIndex />
            </ProtectedRoute>
          }
        />

        <Route
          path="/finance-manager/*"
          element={
            <ProtectedRoute allowedRoles={["finance_manager"]}>
              <FinanceManagerIndex />
            </ProtectedRoute>
          }
        />

        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
